import { FunctionComponent, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { StarjackTable } from '@starjack/shared-components';
import { usePagination, FormDrawer } from '@starjack/shared-components';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import {
    B2BUserWithCredentials,
    createEmptyB2BUserWithCredentials,
} from '@/models/b2b';
import { useGetB2BUsersQuery } from '@/api/v2/b2b';
import { useAuthentication } from '@/hooks/useAuthentication';
import { B2BDrawer } from '@/pages/b2b/components/B2BDrawer';
import { B2BDeleteDialog } from './B2BDeleteDialog';

export const B2BContent: FunctionComponent = () => {
    const theme = useTheme();
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const [b2bToEdit, setB2bToEdit] = useState<{
        toEdit: B2BUserWithCredentials;
        isOpen: boolean;
    }>({ toEdit: null, isOpen: false });
    const [b2bToDelete, setB2bToDelete] = useState<{
        toDelete: B2BUserWithCredentials;
        isOpen: boolean;
    }>({ toDelete: null, isOpen: false });

    const { data, isLoading: b2bUsersLoading, error } = useGetB2BUsersQuery();

    const sortedB2bUsers = useMemo(
        () =>
            [...(data?.b2bUsers || [])].sort((a, b) =>
                a.name.localeCompare(b.name)
            ),
        [data]
    );
    const { currentItems, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({
            items: sortedB2bUsers,
            itemsPerPage: 10,
        });

    const closeDrawer = () => {
        setB2bToEdit((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                B2B
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <Button
                        onClick={() => {
                            setB2bToEdit({
                                isOpen: true,
                                toEdit: createEmptyB2BUserWithCredentials(
                                    liftOperatorId
                                ),
                            });
                        }}
                        variant='contained'
                    >
                        + Neue Verkaufsstelle für B2B anlegen
                    </Button>
                </Box>
                <StarjackTable<B2BUserWithCredentials>
                    count={data?.b2bUsers.length}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Name der B2B Partnerfirma',
                            itemKey: 'name',
                        },
                        {
                            label: 'Benutzername',
                            itemKey: 'username',
                        },

                        {
                            label: 'BEI Benutzername',
                            itemTransform: (item) =>
                                item?.beiUsername ? item.beiUsername : '-',
                        },
                        {
                            label: 'BEI Passwort',
                            itemTransform: (item) =>
                                item?.beiPassword ? item.beiPassword : '-',
                        },
                        {
                            label: 'Status',
                            itemTransform: (item) => {
                                return item.enabled
                                    ? 'Aktiviert'
                                    : 'Deaktiviert';
                            },
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (item) => (
                                <Stack spacing={2} direction='row'>
                                    <IconButton
                                        aria-label='Bearbeiten'
                                        onClick={() => {
                                            setB2bToEdit({
                                                toEdit: item,
                                                isOpen: true,
                                            });
                                        }}
                                    >
                                        <SvgIcon fontSize='small'>
                                            <EditIcon />
                                        </SvgIcon>
                                    </IconButton>

                                    <IconButton
                                        aria-label='Löschen'
                                        onClick={() => {
                                            setB2bToDelete({
                                                toDelete: item,
                                                isOpen: true,
                                            });
                                        }}
                                    >
                                        <SvgIcon fontSize='small'>
                                            <DeleteIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Stack>
                            ),
                        },
                    ]}
                    itemId={'id'}
                    loading={b2bUsersLoading}
                    error={error}
                    onPageChange={setNextPage}
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                />

                <FormDrawer
                    anchor='right'
                    open={b2bToEdit.isOpen}
                    onClose={closeDrawer}
                >
                    <B2BDrawer
                        b2b={b2bToEdit.toEdit || null}
                        onClose={closeDrawer}
                    />
                </FormDrawer>

                <B2BDeleteDialog
                    onClose={() => {
                        setB2bToDelete((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    open={!!b2bToDelete.isOpen}
                    b2b={b2bToDelete.toDelete}
                />
            </Stack>
        </>
    );
};
